import { useCallback, useId, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { APIKeyCreatedDTO } from '@/api/trafikportalen';
import { DialogWindow } from '@/components/Dialog';
import Button from '@/components/core/Button';
import Input from '@/components/core/Input';
import { WarningBanner } from '@/components/core/WarningBanner';
import { toast } from '@/hooks/useToast';
import { IconCheck, IconCopy } from '@/icons';

interface CreatedApiKeyDialogProps {
  apiKey: APIKeyCreatedDTO;
  onClose: () => void;
}

export const CreatedApiKeyDialog = ({
  apiKey,
  onClose,
}: CreatedApiKeyDialogProps) => {
  const { formatMessage } = useIntl();
  const keyId = useId();
  const warningId = useId();
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(apiKey.key);
      setCopied(true);
      toast.success(
        {
          title: formatMessage({
            defaultMessage: 'API-nyckeln kopierades',
          }),
        },
        { autoClose: 3000 },
      );
    } catch {
      toast.error({
        title: formatMessage({
          defaultMessage: 'Nyckeln kunde inte kopieras',
        }),
        description: formatMessage({
          defaultMessage: 'Markera den och kopiera manuellt.',
        }),
      });
    }
  }, [apiKey.key, formatMessage]);

  return (
    <DialogWindow
      open
      preventClose
      className="max-w-[520px]"
      title={formatMessage({ defaultMessage: 'API-nyckel skapad' })}
      content={
        <div className="flex flex-col gap-6">
          <WarningBanner className="mb-4">
            <span id={warningId}>
              <FormattedMessage defaultMessage="Kopiera nyckeln nu. Den visas inte igen." />
            </span>
          </WarningBanner>
          <Input
            id={keyId}
            type="text"
            label={formatMessage({ defaultMessage: 'API-nyckel' })}
            value={apiKey.key}
            readOnly
            autoFocus
            spellCheck={false}
            autoComplete="off"
            autoCapitalize="off"
            className="font-mono"
            aria-describedby={warningId}
            onFocus={(event) => {
              event.currentTarget.select();
            }}
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              color={copied ? 'green' : undefined}
              startIcon={
                copied ? (
                  <IconCheck aria-hidden="true" />
                ) : (
                  <IconCopy aria-hidden="true" />
                )
              }
              onClick={() => {
                void handleCopy();
              }}
            >
              {copied ? (
                <FormattedMessage defaultMessage="Kopierad" />
              ) : (
                <FormattedMessage defaultMessage="Kopiera" />
              )}
            </Button>
            <Button variant="filled" onClick={onClose}>
              <FormattedMessage defaultMessage="Stäng" />
            </Button>
          </div>
        </div>
      }
    />
  );
};
